import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import Cookies from "js-cookie";
import {
  Container,
  Row,
  Col,
  Card,
  Spinner,
  Button,
  Modal,
  Form,
} from "react-bootstrap";
import { Worker, Viewer } from "@react-pdf-viewer/core";
import "@react-pdf-viewer/core/lib/styles/index.css";
import "../styles/reportpage.css";
import api from "../api.js";

const ReportPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = Cookies.get("access_token");
  const currentuser = Cookies.get("user_id");

  const [report, setReport] = useState(null);
  const [author, setAuthor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [allSubjects, setAllSubjects] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [subjectSearch, setSubjectSearch] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);

  const [editData, setEditData] = useState({
    title: "",
    summary: "",
    key_points: "",
    file: null,
    picture: null,
    related_subjects: [],
    subject: "",
  });

  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // --- Fetch report ---
  useEffect(() => {
    api
      .get(`/report/${id}/`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        setReport(res.data);
        setEditData({
          title: res.data.title,
          summary: res.data.summary || "",
          key_points: res.data.key_points || "",
          file: res.data.file || null,
          picture: res.data.picture || null,
          related_subjects:
            res.data.related_subjects?.map((s) => String(s.id)) || [],
          subject: res.data.subject?.id?.toString() || "",
        });
        setAuthor(res.data.author);
        setLoading(false);
      })
      .catch(() => {
        alert("خطا در دریافت گزارش");
        setLoading(false);
      });
  }, [id]);

  // --- Fetch all subjects ---
  useEffect(() => {
    api
      .get(`/subject/`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        const data = res.data.results || res.data;
        setAllSubjects(data);
      })
      .catch(() => console.error("خطا در دریافت موضوعات"));
  }, []);

  // --- Filters ---
  const filteredSubjects = allSubjects.filter(
    (subject) =>
      subject?.title &&
      subject.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredMainSubjects = allSubjects.filter(
    (subject) =>
      subject?.title &&
      subject.title.toLowerCase().includes(subjectSearch.toLowerCase())
  );

  // --- Edit form submit ---
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData();
    formData.append("title", editData.title);
    formData.append("summary", editData.summary);
    formData.append("key_points", editData.key_points);
    formData.append("subject_id", parseInt(editData.subject));

    if (editData.file instanceof File) formData.append("file", editData.file);
    if (editData.picture instanceof File)
      formData.append("picture", editData.picture);

    editData.related_subjects.forEach((id) =>
      formData.append("related_subjects", id)
    );

    try {
      await api.patch(`/report/${id}/`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });
      setShowEditModal(false);
      window.location.reload();
    } catch (err) {
      alert("خطا در ویرایش گزارش");
    }
  };

  // --- Delete report ---
  const handleDelete = async () => {
    try {
      await api.delete(`/report/${id}/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      navigate(-1);
    } catch (err) {
      alert("خطا در حذف گزارش");
    }
  };

  if (loading)
    return (
      <div className="text-center my-5">
        <Spinner animation="border" />
      </div>
    );

  if (!report) return null;

  return (
    <Container fluid className="field-page-container text-end px-3" dir="rtl">
      <Row className="mb-4 gx-4 gy-3">
        <Col xs={3} md={4}>
          <Card className="p-4 shadow-sm mb-4">
            {/* Title */}
            <h2 className="text-primary2 fw-bold mb-3">{report.title}</h2>

            {/* Summary */}
            {report.summary && (
              <div className="mb-3">
                <h6 className="fw-bold">خلاصه:</h6>
                <p>{report.summary}</p>
              </div>
            )}

            {/* Key Points */}
            {report.key_points && (
              <div className="mb-3">
                <h6 className="fw-bold">نکات کلیدی:</h6>
                <p>{report.key_points}</p>
              </div>
            )}

            {/* Main Subject */}
            <h6 className="text-muted mb-1">موضوع :</h6>
            <Link
              to={`/subject/${report.subject.id}`}
              className="btn btn-edit mb-3 mt-2"
            >
              {report.subject.title}
            </Link>

            {/* Related Subjects */}
            {report.related_subjects &&
              report.related_subjects.length > 0 && (
                <>
                  <h6 className="text-muted mb-1 mt-1">موضوعات مرتبط:</h6>
                  <div className="d-flex flex-wrap gap-2 mb-3">
                    {report.related_subjects.map((subj) => (
                      <Link
                        key={subj.id}
                        to={`/subject/${subj.id}`}
                        className="btn btn-delete btn-sm btn-outline-info"
                      >
                        {subj.title}
                      </Link>
                    ))}
                  </div>
                </>
              )}

            {/* Created At */}
            <p className="text-muted small mb-2">
              تاریخ ایجاد:{" "}
              {new Date(report.created_at).toLocaleDateString("fa-IR")}
            </p>

            {/* Author */}
            <p className="text-muted small mb-1">
              نویسنده: {report.author?.username || "نامشخص"}
            </p>

            {/* View Count */}
            <p className="text-muted small mb-0">
              تعداد بازدید: {report.view_count.toLocaleString("fa-IR")}
            </p>

            {/* Edit/Delete buttons */}
            {author?.id == currentuser && (
              <div className="d-flex gap-2 justify-content-center mt-5">
                <Button
                  className="btn-edit"
                  variant="outline-primary"
                  onClick={() => setShowEditModal(true)}
                >
                  ویرایش
                </Button>
                <Button
                  className="btn-delete"
                  variant="outline-danger"
                  onClick={() => setShowDeleteModal(true)}
                >
                  حذف
                </Button>
              </div>
            )}
          </Card>
        </Col>

        <Col md={8}>
          {report.file && (
            <div className="mb-4">
              <div style={{ height: "600px", border: "1px solid #ccc" }}>
                <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.6.172/build/pdf.worker.min.js">
                  <Viewer fileUrl={report.file} />
                </Worker>
              </div>
            </div>
          )}
        </Col>
      </Row>

      {/* --- Edit Modal --- */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered>
        <Modal.Header closeButton />
        <Modal.Body>
          <Form onSubmit={handleEditSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>عنوان</Form.Label>
              <Form.Control
                type="text"
                value={editData.title}
                onChange={(e) =>
                  setEditData({ ...editData, title: e.target.value })
                }
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>خلاصه</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={editData.summary}
                onChange={(e) =>
                  setEditData({ ...editData, summary: e.target.value })
                }
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>نکات کلیدی</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={editData.key_points}
                onChange={(e) =>
                  setEditData({ ...editData, key_points: e.target.value })
                }
              />
            </Form.Group>

            {/* ✅ Combined Searchable Dropdown for Main Subject */}
            <Form.Group className="mb-3 position-relative">
              <Form.Label>موضوع اصلی</Form.Label>
              <Form.Control
                type="text"
                placeholder="جستجو و انتخاب موضوع..."
                value={
                  allSubjects.find((s) => String(s.id) === editData.subject)?.title ||
                  subjectSearch
                }
                onChange={(e) => {
                  setSubjectSearch(e.target.value);
                  setEditData({ ...editData, subject: "" });
                }}
                onFocus={() => setShowDropdown(true)}
                onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
              />
              {showDropdown && (
                <div
                  className="position-absolute bg-white border rounded shadow-sm w-100 mt-1"
                  style={{
                    maxHeight: "200px",
                    overflowY: "auto",
                    zIndex: 1000,
                  }}
                >
                  {filteredMainSubjects.length > 0 ? (
                    filteredMainSubjects.map((subject) => (
                      <div
                        key={subject.id}
                        className="p-2"
                        style={{
                          cursor: "pointer",
                          backgroundColor:
                            String(subject.id) === editData.subject
                              ? "#f8f9fa"
                              : "white",
                        }}
                        onMouseDown={() => {
                          setEditData({ ...editData, subject: String(subject.id) });
                          setSubjectSearch(subject.title);
                          setShowDropdown(false);
                        }}
                      >
                        {subject.title}
                      </div>
                    ))
                  ) : (
                    <div className="p-2 text-muted">موضوعی یافت نشد</div>
                  )}
                </div>
              )}
            </Form.Group>

            {/* --- File Uploads --- */}
            <Form.Group className="mb-3">
              <Form.Label>فایل جدید (PDF)</Form.Label>
              <Form.Control
                type="file"
                accept="application/pdf"
                onChange={(e) =>
                  setEditData({ ...editData, file: e.target.files[0] })
                }
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>تصویر</Form.Label>
              <Form.Control
                type="file"
                accept="image/*"
                onChange={(e) =>
                  setEditData({ ...editData, picture: e.target.files[0] })
                }
              />
            </Form.Group>

            {/* --- Related Subjects Search --- */}
            <Form.Group className="mb-3">
              <Form.Label>موضوعات مرتبط</Form.Label>
              <Form.Control
                type="text"
                placeholder="جستجو در موضوعات..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="mb-2"
              />
              <div className="scrollable-checkbox-list">
                {filteredSubjects.length > 0 ? (
                  filteredSubjects.map((subject) => (
                    <Form.Check
                      key={subject.id}
                      type="checkbox"
                      label={subject.title}
                      checked={editData.related_subjects.includes(
                        String(subject.id)
                      )}
                      onChange={(e) => {
                        const isChecked = e.target.checked;
                        const id = String(subject.id);
                        setEditData((prev) => ({
                          ...prev,
                          related_subjects: isChecked
                            ? [...prev.related_subjects, id]
                            : prev.related_subjects.filter(
                                (sid) => sid !== id
                              ),
                        }));
                      }}
                    />
                  ))
                ) : (
                  <div className="text-muted text-center">
                    موضوعی یافت نشد.
                  </div>
                )}
              </div>
            </Form.Group>

            <div className="d-flex justify-content-end gap-2">
              <Button
                className="btn-edit"
                onClick={() => setShowEditModal(false)}
              >
                انصراف
              </Button>
              <Button className="btn-submit" type="submit">
                ثبت تغییرات
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* --- Delete Modal --- */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton />
        <Modal.Body>آیا از حذف این گزارش مطمئن هستید؟</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            لغو
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            حذف
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default ReportPage;
